import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';

import { StripeVolumePricingConfig } from '../../../../../shared/graphql/graphql';
import { getShortAreaUnit } from '../../../../../shared/features/areaUnit/helpers/functions/areaUnit';
import CalculatorTable from '../../../../components/CalculatorTable';
import {
  formatNumber,
  isNumber,
} from '../../../../../shared/helpers/functions/number';
import rootStore from '../../../rootStore/RootStore';
import useSubscriptionExample from '../../hooks/useSubscriptionExample';
import usePriceConfig from '../../../../../shared/features/priceConfig/hooks/usePriceConfig';
import { getCurrencyForAreaUnit } from '../../../../../shared/helpers/functions/currency';
import { MIN_ANNUAL_AREA_AMOUNTS } from '../../../../../shared/helpers/constants/areaAmounts';
import AmplitudeAnalytics from '../../../../../shared/services/AmplitudeAnalytics';

const PlanCalculatorTable = observer(() => {
  const { config } = usePriceConfig(rootStore);
  const { uiStateStore, areaUnitStore } = rootStore;
  const { areaUnit } = areaUnitStore;

  const currency = getCurrencyForAreaUnit(areaUnit);
  const volumePricing = config?.AnnualSubscription
    ?.FullAccessToEssentialFunctionalities?.prices[currency]
    ?.volumePricing as StripeVolumePricingConfig[];
  const userSeatCost =
    config?.AnnualSubscription.AdditionalUserSeat?.prices[currency]?.price ?? 0;
  const planetImageryCost =
    config?.AnnualSubscription.PlanetImagery?.prices[currency]?.price ?? 0;
  const minAreaAmount = MIN_ANNUAL_AREA_AMOUNTS[areaUnit];

  const {
    annualPlan: {
      area,
      additionalSeats: userSeatsAmount,
      planetImagery: planetImageryAmount,
    },
    setAnnualPlanArea: setArea,
    setAnnualPlanSeats: setUserSetsAmount,
    setAnnualPlanPlanetImagery: setPlanetImageryAmount,
  } = uiStateStore;

  const { t } = useTranslation();
  const shortAreaUnit = getShortAreaUnit(areaUnit);
  const { totalCost, tiers, seats, planet } = useSubscriptionExample({
    subscription: {
      amount: area ? parseInt(area, 10) : 0,
      volumePricing,
    },
    seats: {
      amount: parseInt(userSeatsAmount, 10),
      price: userSeatCost,
    },
    planet: {
      amount: parseInt(planetImageryAmount, 10),
      price: planetImageryCost,
    },
  });

  const handleAreaInputChange = (value: string) => {
    const parsedValue = parseInt(value, 10);

    if (isNumber(parsedValue)) {
      setArea(parsedValue.toString());
    } else {
      setArea('');
    }
  };

  const handleAreaInputBlur = () => {
    const parsedValue = parseInt(area, 10);

    if (parsedValue && parsedValue >= minAreaAmount) {
      setArea(parsedValue.toString());
    } else {
      setArea(minAreaAmount.toString());
    }

    AmplitudeAnalytics.trackAnnualPricingCalculated({
      area: parsedValue,
      areaUnit,
    });
  };

  const handleUserSeatsInputChange = (value: string) => {
    const parsedValue = parseInt(value, 10);

    if (isNumber(parsedValue)) {
      setUserSetsAmount(parsedValue.toString());
    } else {
      setUserSetsAmount('');
    }
  };

  const handleUserSeatsInputBlur = () => {
    const parsedValue = parseInt(userSeatsAmount, 10);

    if (parsedValue && parsedValue >= 0) {
      setUserSetsAmount(parsedValue.toString());
    } else {
      setUserSetsAmount('0');
    }

    AmplitudeAnalytics.trackAnnualPricingCalculated({
      userSeats: parsedValue,
    });
  };

  const handlePlanetInputChange = (value: string) => {
    const parsedValue = parseInt(value, 10);

    if (isNumber(parsedValue)) {
      setPlanetImageryAmount(parsedValue.toString());
    } else {
      setPlanetImageryAmount('');
    }
  };

  const handlePlanetInputBlur = () => {
    const parsedValue = parseInt(planetImageryAmount, 10);

    if (parsedValue && parsedValue >= 0) {
      setPlanetImageryAmount(parsedValue.toString());
    } else {
      setPlanetImageryAmount('0');
    }

    AmplitudeAnalytics.trackAnnualPricingCalculated({
      planetImagery: parsedValue,
      areaUnit,
    });
  };

  const rows = [
    {
      title: t('plans.annual.title'),
      quantity: parseInt(area, 10) ?? 0,
      quantityUnit: shortAreaUnit,
      onQuantityChange: handleAreaInputChange,
      onQuantityInputBlur: handleAreaInputBlur,
    },
    ...tiers.map((tier, index) => ({
      title:
        index === 0
          ? t('subscription-table.annual-subscription-first-line', {
              amount: formatNumber(tier.max),
              areaUnit: shortAreaUnit,
            })
          : t('subscription-table.annual-subscription-next-line', {
              from: formatNumber(tier.min),
              to: formatNumber(tier.max),
              areaUnit: shortAreaUnit,
            }),
      quantity: tier.amount,
      quantityUnit: shortAreaUnit,
      price: tier.unitPrice,
      amount: tier.totalPrice,
    })),
    {
      title: t('subscription-table.user-seat'),
      quantity: seats.amount ?? 0,
      quantityUnit: '',
      price: seats.price,
      amount: seats.cost,
      onQuantityChange: handleUserSeatsInputChange,
      onQuantityInputBlur: handleUserSeatsInputBlur,
    },
    {
      title: t('subscription-table.planet-imagery'),
      quantity: planet.amount ?? 0,
      quantityUnit: shortAreaUnit,
      price: planet.price,
      amount: planet.cost,
      onQuantityChange: handlePlanetInputChange,
      onQuantityInputBlur: handlePlanetInputBlur,
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {t('plans.annual.pricing-calculator.title')}
      </Typography>
      <CalculatorTable
        areaUnit={areaUnit}
        rows={rows}
        total={{
          title: t('plan-select.pricing-example.total-annual'),
          amount: totalCost,
        }}
      />
    </Box>
  );
});

export default PlanCalculatorTable;
